import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, Search, Plus, MoreHorizontal, Pin, Folder, Trash2, Share } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { format } from "date-fns";
import type { Note, NoteFolder } from "../../types";

interface NotesAppProps {
  onClose: () => void;
}

export function NotesApp({ onClose }: NotesAppProps) {
  const [selectedNote, setSelectedNote] = useState<string | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch data from Convex
  const notes = useQuery(api.notes.getAll) || [];
  const folders = useQuery(api.notes.getFolders) || [];

  // Filter notes based on folder and search
  const filteredNotes = notes.filter((note) => {
    const matchesFolder = selectedFolder
      ? note.folderId === selectedFolder
      : true;
    const matchesSearch = searchQuery
      ? note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        note.content.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    return matchesFolder && matchesSearch;
  });

  // Sort notes: pinned first, then by modification date
  const sortedNotes = filteredNotes.sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return (
      new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()
    );
  });

  // Group notes by date
  const groupedNotes = sortedNotes.reduce((acc, note) => {
    const date = new Date(note.modifiedAt);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let groupName: string;
    if (date.toDateString() === today.toDateString()) {
      groupName = "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      groupName = "Yesterday";
    } else if (date.getFullYear() === today.getFullYear()) {
      groupName = format(date, "MMMM d");
    } else {
      groupName = format(date, "MMMM d, yyyy");
    }

    if (!acc[groupName]) acc[groupName] = [];
    acc[groupName].push(note);
    return acc;
  }, {} as Record<string, Note[]>);

  const selectedNoteData = notes.find((n) => n._id === selectedNote);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="absolute inset-0 z-40 flex bg-black"
    >
      {/* Sidebar - Notes List */}
      <div
        className={`flex flex-col ${selectedNote ? "hidden" : "flex"} w-full border-r border-white/10 bg-black sm:flex sm:w-80`}
      >
        {/* Status Bar */}
        <div className="flex items-center justify-between px-6 pt-3 text-white">
          <span className="text-sm font-semibold">9:41</span>
          <div className="flex items-center gap-1">
            <div className="h-4 w-4 rounded-full border-2 border-white" />
            <div className="h-4 w-4 rounded-full border-2 border-white" />
            <div className="h-2 w-6 rounded-sm border-2 border-white bg-white" />
          </div>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center justify-between px-4 py-3">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="flex items-center gap-1 text-ios-yellow"
          >
            <ChevronLeft className="h-6 w-6" />
            <span className="text-lg">Memories</span>
          </motion.button>

          <h1 className="text-lg font-semibold text-white">
            {selectedFolder
              ? folders.find((f) => f._id === selectedFolder)?.name || "Folder"
              : "All Notes"}
          </h1>

          <motion.button
            whileTap={{ scale: 0.9 }}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10"
          >
            <Plus className="h-5 w-5 text-white" />
          </motion.button>
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2">
            <Search className="h-4 w-4 text-white/50" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search"
              className="flex-1 bg-transparent text-white placeholder-white/50 outline-none"
            />
          </div>
        </div>

        {/* Folder Filter */}
        <div className="flex items-center gap-2 overflow-x-auto border-b border-white/10 px-4 pb-3">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setSelectedFolder(null)}
            className={`whitespace-nowrap rounded-full px-4 py-1 text-sm ${
              selectedFolder === null
                ? "bg-ios-yellow text-black"
                : "bg-white/10 text-white"
            }`}
          >
            All Notes
          </motion.button>
          {folders.map((folder) => (
            <motion.button
              key={folder._id}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedFolder(folder._id)}
              className={`whitespace-nowrap rounded-full px-4 py-1 text-sm ${
                selectedFolder === folder._id
                  ? "bg-ios-yellow text-black"
                  : "bg-white/10 text-white"
              }`}
            >
              {folder.name}
            </motion.button>
          ))}
        </div>

        {/* Notes List */}
        <div className="flex-1 overflow-y-auto">
          {sortedNotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-500">
                <Plus className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-white">No Notes</h2>
              <p className="mt-1 text-sm text-white/50">
                Create your first note to get started
              </p>
            </div>
          ) : (
            Object.entries(groupedNotes).map(([groupName, groupNotes]) => (
              <div key={groupName}>
                <h3 className="sticky top-0 bg-black px-4 py-2 text-xs font-semibold uppercase text-white/50 backdrop-blur">
                  {groupName}
                </h3>
                <div className="divide-y divide-white/5">
                  {groupNotes.map((note, index) => (
                    <motion.button
                      key={note._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => setSelectedNote(note._id)}
                      className="flex w-full flex-col gap-1 px-4 py-3 text-left hover:bg-white/5"
                    >
                      <div className="flex items-start justify-between">
                        <h4 className="flex-1 truncate text-base font-medium text-white">
                          {note.isPinned && (
                            <Pin className="mr-1 inline-block h-3 w-3 fill-yellow-500 text-yellow-500" />
                          )}
                          {note.title || "Untitled Note"}
                        </h4>
                        <span className="ml-2 text-xs text-white/40">
                          {format(new Date(note.modifiedAt), "h:mm a")}
                        </span>
                      </div>
                      <p className="line-clamp-2 text-sm text-white/50">
                        {note.content.substring(0, 100)}
                        {note.content.length > 100 ? "..." : ""}
                      </p>
                    </motion.button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Home Indicator */}
        <div className="pb-2 pt-1">
          <div className="mx-auto h-1 w-32 rounded-full bg-white/30" />
        </div>
      </div>

      {/* Note Detail View */}
      <AnimatePresence>
        {selectedNoteData && (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            className="absolute inset-0 flex flex-col bg-black sm:relative sm:flex"
          >
            {/* Navigation Bar */}
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setSelectedNote(null)}
                className="flex items-center gap-1 text-ios-yellow sm:hidden"
              >
                <ChevronLeft className="h-6 w-6" />
                <span className="text-lg">Notes</span>
              </motion.button>

              <span className="text-xs text-white/50 sm:hidden">
                {format(new Date(selectedNoteData.modifiedAt), "MMMM d, yyyy")}
              </span>

              <div className="flex-1 sm:hidden" />

              <div className="flex items-center gap-2">
                {selectedNoteData.isPinned && (
                  <Pin className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                )}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  className="p-2 text-white/50 hover:text-white"
                >
                  <Share className="h-5 w-5" />
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  className="p-2 text-white/50 hover:text-white"
                >
                  <MoreHorizontal className="h-5 w-5" />
                </motion.button>
              </div>
            </div>

            {/* Note Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <h1 className="text-2xl font-semibold text-white sm:text-3xl">
                {selectedNoteData.title || "Untitled Note"}
              </h1>
              <div className="mt-2 flex items-center gap-2 text-xs text-white/40">
                <span>
                  {format(
                    new Date(selectedNoteData.modifiedAt),
                    "MMMM d, yyyy 'at' h:mm a"
                  )}
                </span>
              </div>
              <div className="mt-4 whitespace-pre-wrap text-base text-white/90 leading-relaxed">
                {selectedNoteData.content}
              </div>

              {/* Attachments */}
              {selectedNoteData.attachments &&
                selectedNoteData.attachments.length > 0 && (
                  <div className="mt-6 space-y-4">
                    {selectedNoteData.attachments.map((attachment, index) => (
                      <div key={index}>
                        {attachment.type === "image" && attachment.url && (
                          <img
                            src={attachment.url}
                            alt="Note attachment"
                            className="max-h-64 rounded-lg"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </div>

            {/* Bottom Toolbar */}
            <div className="border-t border-white/10 px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="text-xs text-white/40">
                  {selectedNoteData.content.length} characters
                </div>
                <div className="flex items-center gap-4">
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    className="text-white/50 hover:text-white"
                  >
                    <Trash2 className="h-5 w-5" />
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Home Indicator */}
            <div className="pb-2 pt-1">
              <div className="mx-auto h-1 w-32 rounded-full bg-white/30" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}